import type { LocalizationResource } from '@clerk/shared/types';

export const enUS: LocalizationResource = {
  locale: 'en-US',
  apiKeys: {
    action__add: 'Add new key',
    action__search: 'Search keys',
    copySecret: {
      formButtonPrimary__copyAndClose: 'Copy & Close',
      formHint: "For security reasons, we won't allow you to view it again later.",
      formTitle: 'Copy your "{{name}}" API Key now',
    },
    createdAndExpirationStatus__expiresOn:
      "Created {{ createdDate | shortDate('en-US') }} • Expires {{ expiresDate | longDate('en-US') }}",
    createdAndExpirationStatus__never: "Created {{ createdDate | shortDate('en-US') }} • Never expires",
    detailsTitle__emptyRow: 'No API keys found',
    formButtonPrimary__add: 'Create key',
    formFieldCaption__expiration__expiresOn: 'Expiring {{ date }}',
    formFieldCaption__expiration__never: 'This key will never expire',
    formFieldOption__expiration__180d: '180 Days',
    formFieldOption__expiration__1d: '1 Day',
    formFieldOption__expiration__1y: '1 Year',
    formFieldOption__expiration__30d: '30 Days',
    formFieldOption__expiration__60d: '60 Days',
    formFieldOption__expiration__7d: '7 Days',
    formFieldOption__expiration__90d: '90 Days',
    formFieldOption__expiration__never: 'Never',
    formHint: 'Provide a name to generate a new key. You’ll be able to revoke it anytime.',
    formTitle: 'Add new API key',
    lastUsed__days: '{{days}}d ago',
    lastUsed__hours: '{{hours}}h ago',
    lastUsed__minutes: '{{minutes}}m ago',
    lastUsed__months: '{{months}}mo ago',
    lastUsed__seconds: '{{seconds}}s ago',
    lastUsed__years: '{{years}}y ago',
    menuAction__revoke: 'Revoke key',
    revokeConfirmation: {
      confirmationText: 'Revoke',
      formButtonPrimary__revoke: 'Revoke key',
      formHint: 'Are you sure you want to delete this Secret key?',
      formTitle: 'Revoke "{{apiKeyName}}" secret key?',
      inputLabel: 'Type "Revoke" to confirm',
    },
    tableHeader__actions: 'Actions',
    tableHeader__lastUsed: 'Last used',
    tableHeader__name: 'Name',
  },
  backButton: 'Back',
  badge__activePlan: 'Active',
  badge__banned: 'Banned',
  badge__canceledEndsAt: "Canceled • Ends {{ date | shortDate('en-US') }}",
  badge__currentPlan: 'Current plan',
  badge__default: 'Default',
  badge__endsAt: "Ends {{ date | shortDate('en-US') }}",
  badge__expired: 'Expired',
  badge__freeTrial: 'Free trial',
  badge__otherImpersonatorDevice: 'Other impersonator device',
  badge__pastDueAt: "Past due {{ date | shortDate('en-US') }}",
  badge__pastDuePlan: 'Past due',
  badge__primary: 'Primary',
  badge__renewsAt: "Renews {{ date | shortDate('en-US') }}",
  badge__requiresAction: 'Requires action',
  badge__startsAt: "Starts {{ date | shortDate('en-US') }}",
  badge__thisDevice: 'This device',
  badge__trialEndsAt: "Trial ends {{ date | shortDate('en-US') }}",
  badge__unverified: 'Unverified',
  badge__upcomingPlan: 'Upcoming',
  badge__userDevice: 'User device',
  badge__you: 'You',
  billing: {
    accountCredit: 'Account credit',
    addPaymentMethod__label: 'Add payment method',
    alwaysFree: 'Always free',
    annually: 'Annually',
    availableFeatures: 'Available features',
    billedAnnually: 'Billed annually',
    billedAnnuallyOnly: 'Only billed annually',
    billedMonthly: 'Billed monthly',
    billedMonthlyOnly: 'Only billed monthly',
    cancelFreeTrial: 'Cancel free trial',
    cancelFreeTrialAccessUntil:
      "Your trial will stay active until {{ date | longDate('en-US') }}. After that, you'll lose access to trial features. You won't be charged.",
    cancelFreeTrialTitle: 'Cancel free trial for {{plan}} plan?',
    cancelSubscription: 'Cancel subscription',
    cancelSubscriptionAccessUntil:
      "You can keep using '{{plan}}' features until {{ date | longDate('en-US') }}, after which you will no longer have access.",
    cancelSubscriptionNoCharge: 'You will not be charged for this subscription.',
    cancelSubscriptionPastDue:
      'Your subscription will end immediately and you will lose access to all plan features. You will be asked to pay the past due amount on your next subscription.',
    cancelSubscriptionTitle: 'Cancel {{plan}} Subscription?',
    cannotSubscribeMonthly:
      'You cannot subscribe to this plan by paying monthly. To subscribe to this plan, you need to choose to pay annually.',
    cannotSubscribeUnrecoverable:
      'You cannot subscribe to this plan. Your existing subscription is more expensive than this plan.',
    checkout: {
      description__paymentSuccessful: 'Your payment was successful.',
      description__subscriptionSuccessful: 'Your new subscription is all set.',
      downgradeNotice:
        'You will keep your current subscription and its features until the end of the billing cycle, then you will be switched to this subscription.',
      emailForm: {
        subtitle: 'Before you can complete your purchase you must add an email address where receipts will be sent.',
        title: 'Add an email address',
      },
      lineItems: {
        title__freeTrialEndsAt: 'Trial ends on',
        title__paymentMethod: 'Payment method',
        title__statementId: 'Statement ID',
        title__subscriptionBegins: 'Subscription begins',
        title__totalPaid: 'Total paid',
      },
      pastDueNotice: 'Your previous subscription was past due, with no payment.',
      perMonth: 'per month',
      title: 'Checkout',
      title__paymentSuccessful: 'Payment was successful!',
      title__subscriptionSuccessful: 'Success!',
      title__trialSuccess: 'Trial successfully started!',
      totalDueAfterTrial: 'Total Due after trial ends in {{days}} days',
      totalDuePerPeriod: 'Total Due per period',
    },
    credit: 'Credit',
    creditRemainder: 'Credit for the remainder of your current subscription.',
    defaultFreePlanActive: "You're currently on the Free plan",
    free: 'Free',
    getStarted: 'Get started',
    highlightedPlanBadge: 'Popular',
    keepFreeTrial: 'Keep free trial',
    keepSubscription: 'Keep subscription',
    manage: 'Manage',
    manageSubscription: 'Manage subscription',
    month: 'Month',
    monthAbbreviation: 'mo',
    monthPerUnit: 'Month per {{unitName}}',
    monthly: 'Monthly',
    pastDue: 'Past due',
    pay: 'Pay {{amount}}',
    payerCreditRemainder: 'Credit from account balance.',
    paymentMethod: {
      applePayDescription: {
        annual: 'Annual payment',
        monthly: 'Monthly payment',
      },
      dev: {
        anyNumbers: 'Any numbers',
        cardNumber: 'Card number',
        cvcZip: 'CVC, ZIP',
        developmentMode: 'Development mode',
        expirationDate: 'Expiration date',
        testCardInfo: 'Test card information',
      },
    },
    paymentMethods__label: 'Payment Methods',
    pricingTable: {
      billingCycle: 'Billing cycle',
      included: 'Included',
      seatCost: {
        additionalSeats: '({{additionalTierFeePerBlockAmount}}/{{periodAbbreviation}} for additional)',
        freeUpToSeats: 'Free up to {{endsAfterBlock}} seats',
        includedSeats: '{{includedSeats}} seats included',
        perSeat: '{{feePerBlockAmount}}/{{periodAbbreviation}} per seat',
        tooltip: {
          additionalSeatsEach: 'Additional seats are {{feePerBlockAmount}}/{{period}} each.',
          firstSeatsIncludedInPlan: 'First {{endsAfterBlock}} seats are included in the plan.',
          freeForUpToSeats: 'Free for up to {{endsAfterBlock}} seats.',
        },
        unlimitedSeats: 'Unlimited seats',
        upToSeats: 'Up to {{endsAfterBlock}} seats',
      },
    },
    proratedDiscount: 'Prorated discount',
    prorationCredit: 'Prorated credit',
    reSubscribe: 'Resubscribe',
    seatBreakdownIncludedPlural: '{{chargeable}} seats at {{rate}}/mo ({{totalSeats}} total - {{included}} included)',
    seatBreakdownIncludedSingular: '1 seat at {{rate}}/mo ({{totalSeats}} total - {{included}} included)',
    seatBreakdownPlural: '{{chargeable}} seats at {{rate}}/mo',
    seatBreakdownSingular: '1 seat at {{rate}}/mo',
    seats: 'Seats',
    seatsWithLimit: 'Seats (up to {{limit}})',
    seeAllFeatures: 'See all features',
    startFreeTrial: 'Start free trial',
    startFreeTrial__days: 'Start {{days}}-day free trial',
    subscribe: 'Subscribe',
    subscriptionDetails: {
      beginsOn: 'Begins on',
      currentBillingCycle: 'Current billing cycle',
      endsOn: 'Ends on',
      firstPaymentAmount: 'First payment amount',
      firstPaymentOn: 'First payment on',
      nextPaymentAmount: 'Next payment amount',
      nextPaymentOn: 'Next payment on',
      pastDueAt: 'Past due on',
      renewsAt: 'Renews at',
      subscribedOn: 'Subscribed on',
      title: 'Subscription',
      trialEndsOn: 'Trial ends on',
      trialStartedOn: 'Trial started on',
    },
    subtotal: 'Subtotal',
    subtotalRenewal: 'Subtotal per period',
    switchPlan: 'Switch to this plan',
    switchToAnnual: 'Switch to annual',
    switchToAnnualWithAnnualPrice: 'Switch to annual {{price}} / year',
    switchToMonthly: 'Switch to monthly',
    switchToMonthlyWithPrice: 'Switch to monthly {{price}} / month',
    totalDue: 'Total due',
    totalDuePerPeriod: 'Total per period',
    totalDueToday: 'Total due today',
    viewFeatures: 'View features',
    viewPayment: 'View payment',
    year: 'Year',
    yearAbbreviation: 'yr',
    yearPerUnit: 'Year per {{unitName}}',
  },
  configureSSO: {
    activate: {
      activateButton: 'Activate SSO',
      activeSubtitle: 'Anyone signing in with {{domain}} must use your identity provider.',
      activeTitle: 'SSO connection is active',
      doneButton: 'Done',
      skipButton: 'Skip for now',
      subtitle:
        'Your SSO connection is ready. Once activated, anyone signing in with {{domain}} must use your identity provider.',
      title: 'SSO connection configured',
    },
    changeProviderDialog: {
      cancelButton: 'Cancel',
      confirmButton: 'Change provider',
      subtitle: 'Switching to {{provider}} will remove your {{currentProvider}} connection and require a new setup.',
      title: 'Change provider to {{provider}}',
    },
    configureStep: {
      activeConnectionWarning: {
        dismiss: 'Dismiss',
        title:
          'This connection is active. Saving changes applies immediately and may disrupt sign-in for current members.',
      },
      attributeMappingTable: {
        badges: {
          optional: 'Optional',
          required: 'Required',
        },
      },
      samlCustom: {
        assignUsersStep: {
          headerSubtitle: 'Assign users or groups to your SAML application',
          paragraph: 'Assign users or groups to your app before they can sign in with SSO.',
        },
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              attributeName: 'Attribute Name',
              userAttribute: 'User Attribute',
            },
            rows: {
              email: {
                attributeName: 'Primary email',
                userAttribute: 'mail',
              },
              firstName: {
                attributeName: 'First name',
                userAttribute: 'firstName',
              },
              lastName: {
                attributeName: 'Last name',
                userAttribute: 'lastName',
              },
            },
          },
          headerSubtitle: 'Map user attributes from your identity provider to your application.',
          paragraph: 'Your SAML response must include the following attributes:',
        },
        createAppStep: {
          createAppInstructions: {
            paragraph:
              'In your identity provider’s dashboard, create a new SAML 2.0 application and use the following service provider details:',
          },
          headerSubtitle: 'Create a new SAML application in your identity provider’s dashboard',
          serviceProviderFields: {
            acsUrl: {
              label: 'Assertion consumer service (ACS) URL',
            },
            spEntityId: {
              label: 'Entity ID',
            },
          },
        },
        identityProviderMetadataStep: {
          headerSubtitle: 'Configure identity provider metadata',
          manual: {
            description: 'In your identity provider SAML application, retrieve these values.',
            issuer: {
              label: 'Issuer',
              placeholder: 'Paste URL here...',
            },
            signOnUrl: {
              label: 'Sign on URL',
              placeholder: 'Paste URL here...',
            },
            signingCertificate: {
              fileUploaded: 'File uploaded',
              label: 'Signing certificate',
              removeFile: 'Remove file',
              replaceFile: 'Replace file',
              uploadFile: 'Upload file',
            },
          },
          metadataUrl: {
            description: 'In your identity provider SAML application, retrieve the metadata URL. Paste it below.',
            label: 'Metadata URL',
            placeholder: 'Paste URL here...',
          },
          modes: {
            ariaLabel: 'Configuration ',
            manual: 'Configure manually',
            metadataUrl: 'Add via metadata',
          },
        },
        mainHeaderTitle: 'Configure your identity provider',
      },
      samlGoogle: {
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              appAttribute: 'App attribute',
              googleAttribute: 'Google attribute',
            },
            rows: {
              email: {
                appAttribute: 'email',
                googleAttribute: 'Primary email',
              },
              firstName: {
                appAttribute: 'firstName',
                googleAttribute: 'First name',
              },
              lastName: {
                appAttribute: 'lastName',
                googleAttribute: 'Last name',
              },
            },
          },
          headerSubtitle: 'Map user attributes from Google Workspace to your application',
          paragraph: 'We expect your SAML response to return the user’s email, first name and last name.',
          step1: 'In the <bold>Google Admin Console</bold>, find the <bold>Attributes</bold> section.',
          step2:
            'Select <bold>Add mapping</bold> for each attribute, and enter the following Google and app attribute:',
        },
        configureUserAccess: {
          assignUsersInstructions: {
            paragraph1:
              "Once the configuration is complete in Google, you'll be redirected to the app's overview page.",
            paragraph2:
              'Google may take up to 24 hours to propagate these changes. The connection will remain inactive until they take effect.',
            step1: 'Open the <bold>User access</bold> section.',
            step2: 'Select <bold>ON for everyone.</bold>',
            step3: 'Select <bold>Save</bold>.',
          },
          headerSubtitle: 'Enable your Google Workspace SAML application',
        },
        createAppStep: {
          createAppInstructions: {
            step1: 'In the side navigation, under <bold>Apps</bold>, select <bold>Web and mobile apps.</bold>',
            step2: 'Select <bold>Add app</bold>, then <bold>Add custom SAML app.</bold>',
            step3: 'Enter an <bold>App name.</bold>',
            step4: 'Select <bold>Continue</bold>.',
            title: 'In Google Workspace, create a new SAML application:',
          },
          headerSubtitle: 'Create a new SAML application in Google Workspace',
        },
        identityProviderMetadataStep: {
          headerSubtitle: 'Add your Google Workspace application metadata',
          manual: {
            description: 'In your Google Workspace app, retrieve these values.',
            issuer: {
              label: 'Entity ID',
              placeholder: 'Paste URL here...',
            },
            signOnUrl: {
              label: 'SSO URL',
              placeholder: 'Paste URL here...',
            },
            signingCertificate: {
              fileUploaded: 'File uploaded',
              label: 'Signing certificate',
              removeFile: 'Remove file',
              replaceFile: 'Replace file',
              uploadFile: 'Upload file',
            },
          },
          metadataFile: {
            description: 'In your Google Workspace app, download the IdP metadata and upload it below.',
            fileUploaded: 'File uploaded',
            label: 'IdP metadata',
            removeFile: 'Remove file',
            replaceFile: 'Replace file',
            uploadFile: 'Upload file',
          },
          modes: {
            ariaLabel: 'Configuration',
            manual: 'Configure manually',
            metadataFile: 'Add via metadata',
          },
        },
        mainHeaderTitle: 'Configure Google Workspace',
        serviceProviderStep: {
          headerSubtitle: 'Configure service provider',
          nameIdInstructions: {
            step1:
              'Under the <bold>Name ID</bold> section, select the <bold>Name ID</bold> format dropdown and select <bold>Email</bold>.',
            step2: 'Select <bold>Continue</bold>',
          },
          paragraph:
            'To configure your service provider, you must add these two fields to your Google Workspace SAML application:',
          serviceProviderFields: {
            acsUrl: {
              label: 'ACS URL',
            },
            spEntityId: {
              label: 'Entity ID',
            },
          },
          title: 'Configure service provider',
        },
      },
      samlMicrosoft: {
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              attribute: 'Attribute',
              claimName: 'Claim name',
              value: 'Value',
            },
            copyClaimName: 'Copy claim name',
            copyClaimNameCopied: 'Copied',
            rows: {
              email: {
                attribute: 'Email address',
                claimName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
                value: 'user.mail',
              },
              firstName: {
                attribute: 'First name',
                claimName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname',
                value: 'user.givenname',
              },
              lastName: {
                attribute: 'Last name',
                claimName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname',
                value: 'user.surname',
              },
            },
          },
          headerSubtitle: 'Set the attributes Microsoft Entra includes in your SAML response',
          step1: 'On the <bold>SAML-based Sign-on</bold> page, find the <bold>Attributes & Claims</bold> section.',
          step2: 'Select <bold>Edit.</bold>',
          title: 'Your SAML response must include the following attributes:',
        },
        createAppStep: {
          assignUsersInstructions: {
            step1: 'In the <bold>Getting Started</bold> section, select the <bold>Assign users and groups.</bold>',
            step2: "Select <bold>Add user/group.</bold> You'll be redirected to the <bold>Add Assignment page.</bold>",
            step3: 'Select the <bold>None Selected link.</bold>',
            step4:
              "Select <bold>Select</bold> at the bottom of the page. You'll be redirected to the <bold>Add Assignment</bold> page.",
            step5: 'Select <bold>Assign</bold>',
            title: 'Assign your users or groups in Microsoft',
          },
          createAppInstructions: {
            step1: 'Sign in to Microsoft Azure Portal and go to <bold>Enterprise applications.</bold>',
            step2:
              "Click <bold>New application.</bold> You'll be redirected to the <bold>Browse Microsoft Entra Gallery</bold> page.",
            step3: 'Select <bold>Create your own application.</bold>',
            step4: {
              label: 'In the modal that opens:',
              subSteps: {
                appName: 'Fill out your application name.',
                create: 'Select <bold>Create</bold>.',
                nonGallery:
                  "Select <bold>Integrate any other application you don't find in the gallery (Non-gallery)</bold>.",
              },
            },
            title: 'Create a new enterprise application',
          },
          headerSubtitle: 'Create a new enterprise application in your Azure Portal',
        },
        identityProviderMetadataStep: {
          headerSubtitle: 'Add your Microsoft Entra application metadata',
          manual: {
            description:
              'On the <bold>SAML-based Sign-on</bold> page, find the <bold>SAML Certificates</bold> section. Retrieve these values and add them below.',
            issuer: {
              label: 'Issuer',
              placeholder: 'Paste URL here...',
            },
            signOnUrl: {
              label: 'Sign on URL',
              placeholder: 'Paste URL here...',
            },
            signingCertificate: {
              fileUploaded: 'File uploaded',
              label: 'Signing certificate',
              removeFile: 'Remove file',
              replaceFile: 'Replace file',
              uploadFile: 'Upload file',
            },
          },
          metadataUrl: {
            description:
              'On the <bold>SAML-based Sign-on</bold> page, find the <bold>SAML Certificates</bold> section and copy the <bold>App Federation Metadata Url</bold>. Paste below.',
            label: 'Metadata URL',
            placeholder: 'Paste URL here...',
          },
          modes: {
            ariaLabel: 'Configuration ',
            manual: 'Configure manually',
            metadataUrl: 'Add via metadata',
          },
        },
        mainHeaderTitle: 'Configure Microsoft Entra',
        serviceProviderStep: {
          headerSubtitle: 'Add service provider configuration to Microsoft Entra',
          serviceProviderFields: {
            acsUrl: {
              label: 'Reply URL (Assertion Consumer Service URL)',
            },
            spEntityId: {
              label: 'Identifier (Entity ID)',
            },
          },
          step1: 'In the side navigation, open the <bold>Manage</bold> dropdown and select Single sign-on.',
          step2: 'In the <bold>Select a single sign-on method</bold> section, select <bold>SAML</bold>.',
          step3: 'Find the <bold>Basic SAML Configuration</bold> section.',
          step4: 'Select <bold>Edit</bold>. The <bold>Basic SAML Configuration</bold> panel will open.',
          step5:
            'Copy the following values into <bold>Identifier (Entity ID)</bold> and <bold>Reply URL (ACS URL)</bold>:',
          step6: 'Select <bold>Save</bold> at the top of the panel. Close the panel.',
          title: 'Add service provider details',
        },
      },
      samlOkta: {
        assignUsersStep: {
          assignUsersInstructions: {
            paragraph: 'Assign users or groups to your Okta application before they can sign in with SSO',
            step1: 'In the Okta dashboard, select the <bold>Assignments</bold> tab.',
            step2:
              'Open the <bold>Assign</bold> dropdown and select <bold>Assign to people</bold> or <bold>Assign to groups</bold>.',
            step3: 'Search for the user or group to assign.',
            step4: 'Click <bold>Assign</bold> next to the user or group.',
            step5: 'Click <bold>Done.</bold>',
          },
          headerSubtitle: 'Assign users to your Okta application',
        },
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              expression: 'Expression',
              name: 'Attribute name',
            },
            rows: {
              email: {
                expression: 'user.profile.email',
                name: 'mail',
              },
              firstName: {
                expression: 'user.profile.firstName',
                name: 'firstName',
              },
              lastName: {
                expression: 'user.profile.lastName',
                name: 'lastName',
              },
            },
          },
          headerSubtitle: 'Set the attributes Okta includes in your SAML response',
          paragraph: 'Your SAML response must include the following attributes:',
          step1: 'In the Okta dashboard, find the <bold>Attribute Statements</bold> section.',
          step2:
            'Select <bold>Add Expression</bold> for each attribute, and enter the following name and expression pairs:',
        },
        createAppStep: {
          completeSamlIntegrationInstructions: {
            step1: 'From the <bold>Feedback</bold>, select <bold>This is an internal app that we have created.</bold>',
            step2: 'Click <bold>Finish</bold> to complete the integration.',
            title: 'Complete the SAML integration',
          },
          createAppInstructions: {
            step1: 'Sign in to Okta and go to <bold>Admin → Applications.</bold>',
            step2: 'Click <bold>Create App Integration.</bold> and select <bold>SAML 2.0.</bold>',
            step3: 'Fill in the General Settings. The app name is required.',
            step4: 'Click <bold>Next</bold> to finish creating the application.',
            title: 'Create a new SAML application in Okta',
          },
          headerSubtitle: 'Create and configure a SAML application in your Okta Dashboard',
          serviceProviderInstructions: {
            paragraph1:
              "After completing <bold>General Settings</bold>, you'll see the <bold>Configure SAML</bold> page.",
            paragraph2: 'Add these two fields to your Okta application to configure your service provider.',
            serviceProviderFields: {
              acsUrl: {
                label: 'Single sign-on URL',
              },
              spEntityId: {
                label: 'Audience URI (SP Entity ID)',
              },
            },
            title: 'Configure service provider',
          },
        },
        identityProviderMetadataStep: {
          headerSubtitle: 'Add your Okta application metadata',
          manual: {
            description: 'In your Okta SAML application, go to the <bold>Sign On</bold> tab and retrieve these values.',
            issuer: {
              label: 'Issuer',
              placeholder: 'Paste URL here...',
            },
            signOnUrl: {
              label: 'Sign on URL',
              placeholder: 'Paste URL here...',
            },
            signingCertificate: {
              fileUploaded: 'File uploaded',
              label: 'Signing certificate',
              removeFile: 'Remove file',
              replaceFile: 'Replace file',
              uploadFile: 'Upload file',
            },
          },
          metadataUrl: {
            description:
              'In your Okta SAML application, go to the <bold>Sign On</bold> tab and retrieve the metadata URL. Paste it below.',
            label: 'Metadata URL',
            placeholder: 'Paste URL here...',
          },
          modes: {
            ariaLabel: 'Configuration',
            manual: 'Configure manually',
            metadataUrl: 'Add via metadata',
          },
        },
        mainHeaderTitle: 'Configure Okta Workforce',
      },
    },
    missingManageEnterpriseConnectionsPermission: {
      subtitle: "Contact your organization's administrator to upgrade your permissions.",
      title: 'You do not have permission to manage Single Sign-on (SSO)',
    },
    navbar: {
      title: 'Configure Single Sign-On (SSO)',
    },
    organizationDomainsStep: {
      domainCard: {
        badge__expired: 'Expired',
        badge__unverified: 'Unverified',
        badge__verified: 'Verified',
        expiredAtLabel:
          "Domain verification expired on {{ date | shortDate('en-US') }}. Verify again to generate a new DNS record.",
        expiredLabel: 'Domain verification expired. Verify again to generate a new DNS record.',
        removeButtonTooltip__lastVerifiedDomain: 'At least one verified domain is required to set up SSO.',
        removeButtonTooltip__lastVerifiedDomainActive: 'At least one verified domain is required to keep SSO enabled.',
        txtRecord: {
          hostLabel: 'Host / Name',
          instructions: "Add this TXT record to your DNS provider. We'll verify automatically once the record is live.",
          typeLabel: 'Type',
          valueLabel: 'Value',
        },
        verifiedAtLabel: "Verified on {{ date | shortDate('en-US') }}",
        verifyAgainButton: 'Verify again',
      },
      domainSuggestion: {
        formButtonPrimary__add: 'Add {{domain}}',
        messageLabel: 'Your email uses {{domain}}. Do you want to add it?',
      },
      formButtonPrimary__add: 'Add',
      formFieldInputPlaceholder__domain: 'Type your domain here and click add to start',
      formFieldLabel__domain: 'Domain',
      removeDomainDialog: {
        cancelButton: 'Cancel',
        removeButton: 'Remove domain',
        subtitle__active:
          "You're about to remove {{domain}} from this enterprise connection. Users won't be able to sign-in with {{domain}} anymore.",
        subtitle__inactive: "You're about to remove {{domain}} from this enterprise connection.",
        title: 'Removing domain',
      },
      subtitle: 'Add and verify ownership of the domains your organization uses to sign in.',
      title: 'Add SSO domains',
    },
    resetConnectionDialog: {
      cancelButton: 'Cancel',
      confirmationFieldLabel: 'Type "{{name}}" below to continue',
      confirmationFieldPlaceholder: '{{name}}',
      resetButton: 'Reset connection',
      subtitle:
        'Are you sure you want to reset the connection? This action is irreversible and you will have to configure all steps again',
      title: 'Reset connection',
    },
    selectProviderStep: {
      saml: {
        customSaml: 'Custom SAML Provider',
        google: 'Google Workspace',
        groupLabel: 'SAML',
        microsoft: 'Microsoft Entra (formerly AD)',
        okta: 'Okta Workforce',
      },
      subtitle: "You'll configure the connection details in the next step",
      title: 'Select your identity provider',
      warning: 'Once a provider is selected you cannot change again until the configuration is over',
    },
    testConfigurationStep: {
      error__noSuccessfulTestRun:
        'You need at least one successful test run before you can continue. Generate a test URL and complete the sign-in flow.',
      subtitle: 'Sign in through the test URL to verify your SSO connection is configured correctly',
      testResults: {
        actionLabel__refresh: 'Refresh logs',
        empty: {
          subtitle: 'Select <bold>Open test URL</bold> to run your first test',
          title: 'No test results',
        },
        polling: 'Waiting for the test run to complete…',
        status__failed: 'Failed',
        status__pending: 'Pending',
        status__success: 'Success',
        title: 'Your test results',
      },
      testRunDetails: {
        howToFix: {
          actionLabel__viewDocumentation: 'View documentation',
          oauth_access_denied: {
            description:
              "This error occurs when the user clicked Cancel or Deny on the OAuth provider's authorization screen, or the provider rejected the authorization request. Verify that the OAuth application credentials (Client ID and Client Secret) are correctly configured.",
          },
          oauth_fetch_user_error: {
            intro: 'To fix this error, follow these steps:',
            step1:
              'Verify that the OAuth scopes configured in your connection settings include the necessary permissions to read user profile information.',
            step2: 'Ensure that the user info endpoint URL is correctly configured.',
          },
          oauth_token_exchange_error: {
            description:
              "Verify that your OAuth application's Client ID and Client Secret are correctly configured and match the credentials from your OAuth provider's dashboard.",
          },
          saml_email_address_domain_mismatch: {
            description:
              'Verify that the user is signing in with an email address that matches one of the allowed domains for this connection. If you need to add additional domains, update the allowed domains in your connection settings.',
          },
          saml_response_relaystate_missing: {
            description:
              'Check that your identity provider is correctly returning the RelayState parameter that was sent in the original request.',
          },
          saml_user_attribute_missing: {
            intro: 'To fix this error, follow these steps:',
            step1: "Access your identity provider's configuration dashboard.",
            step2: "Navigate to your application's SAML settings or attribute mapping configuration.",
            step3: "Ensure that the 'mail' attribute is properly mapped to the user's email address field.",
          },
          sectionTitle: 'How to fix',
        },
        parsedUserInfo: {
          email: 'Email',
          firstName: 'First name',
          sectionTitle: 'Parsed user info',
        },
        runDetails: {
          actionLabel__copied: 'Copied',
          actionLabel__copy: 'Copy message',
          errorCode: 'Error code',
          fullMessage: 'Full message',
          sectionTitle: 'Run details',
          status: 'Status',
          timestamp: 'Timestamp',
        },
        title: 'Test run',
      },
      testUrl: {
        actionLabel__open: 'Open test URL',
      },
      title: 'Test your SSO connection',
    },
  },
  createOrganization: {
    formButtonSubmit: 'Create organization',
    invitePage: {
      formButtonReset: 'Skip',
    },
    title: 'Create organization',
  },
  dates: {
    lastDay: "Yesterday at {{ date | timeString('en-US') }}",
    next6Days: "{{ date | weekday('en-US','long') }} at {{ date | timeString('en-US') }}",
    nextDay: "Tomorrow at {{ date | timeString('en-US') }}",
    numeric: "{{ date | numeric('en-US') }}",
    previous6Days: "Last {{ date | weekday('en-US','long') }} at {{ date | timeString('en-US') }}",
    sameDay: "Today at {{ date | timeString('en-US') }}",
  },
  dividerText: 'or',
  footerActionLink__alternativePhoneCodeProvider: 'Send code via SMS instead',
  footerActionLink__useAnotherMethod: 'Use another method',
  footerPageLink__help: 'Help',
  footerPageLink__privacy: 'Privacy',
  footerPageLink__terms: 'Terms',
  formButtonPrimary: 'Continue',
  formButtonPrimary__verify: 'Verify',
  formFieldAction__forgotPassword: 'Forgot password?',
  formFieldError__matchingPasswords: 'Passwords match.',
  formFieldError__notMatchingPasswords: "Passwords don't match.",
  formFieldError__verificationLinkExpired: 'The verification link expired. Please request a new link.',
  formFieldHintText__optional: 'Optional',
  formFieldHintText__slug: 'A slug is a human-readable ID that must be unique. It’s often used in URLs.',
  formFieldInputPlaceholder__apiKeyDescription: 'Explain why you’re generating this key',
  formFieldInputPlaceholder__apiKeyExpirationDate: 'Select date',
  formFieldInputPlaceholder__apiKeyName: 'Enter your secret key name',
  formFieldInputPlaceholder__backupCode: 'Enter backup code',
  formFieldInputPlaceholder__confirmDeletionUserAccount: 'Delete account',
  formFieldInputPlaceholder__emailAddress: 'Enter your email address',
  formFieldInputPlaceholder__emailAddress_username: 'Enter email or username',
  formFieldInputPlaceholder__emailAddresses: 'example@email.com, example2@email.com',
  formFieldInputPlaceholder__firstName: 'First name',
  formFieldInputPlaceholder__lastName: 'Last name',
  formFieldInputPlaceholder__organizationDomain: 'example.com',
  formFieldInputPlaceholder__organizationDomainEmailAddress: 'you@example.com',
  formFieldInputPlaceholder__organizationName: 'Organization name',
  formFieldInputPlaceholder__organizationSlug: 'my-org',
  formFieldInputPlaceholder__password: 'Enter your password',
  formFieldInputPlaceholder__phoneNumber: 'Enter your phone number',
  formFieldInputPlaceholder__signUpPassword: 'Create a password',
  formFieldInputPlaceholder__username: 'Enter your username',
  formFieldInput__emailAddress_format: 'Example format: name@example.com',
  formFieldLabel__apiKey: 'API key',
  formFieldLabel__apiKeyDescription: 'Description',
  formFieldLabel__apiKeyExpiration: 'Expiration',
  formFieldLabel__apiKeyName: 'Secret key name',
  formFieldLabel__automaticInvitations: 'Enable automatic invitations for this domain',
  formFieldLabel__backupCode: 'Backup code',
  formFieldLabel__confirmDeletion: 'Confirmation',
  formFieldLabel__confirmPassword: 'Confirm password',
  formFieldLabel__currentPassword: 'Current password',
  formFieldLabel__emailAddress: 'Email address',
  formFieldLabel__emailAddress_username: 'Email address or username',
  formFieldLabel__emailAddresses: 'Email addresses',
  formFieldLabel__firstName: 'First name',
  formFieldLabel__lastName: 'Last name',
  formFieldLabel__newPassword: 'New password',
  formFieldLabel__organizationDomain: 'Domain',
  formFieldLabel__organizationDomainDeletePending: 'Delete pending invitations and suggestions',
  formFieldLabel__organizationDomainEmailAddress: 'Verification email address',
  formFieldLabel__organizationDomainEmailAddressDescription:
    'Enter an email address under this domain to receive a code and verify this domain.',
  formFieldLabel__organizationName: 'Name',
  formFieldLabel__organizationSlug: 'Slug',
  formFieldLabel__passkeyName: 'Name of passkey',
  formFieldLabel__password: 'Password',
  formFieldLabel__phoneNumber: 'Phone number',
  formFieldLabel__role: 'Role',
  formFieldLabel__signOutOfOtherSessions: 'Sign out of all other devices',
  formFieldLabel__username: 'Username',
  identityPreviewEditButton__emailAddress: 'Edit email address',
  identityPreviewEditButton__identifier: 'Edit identifier',
  identityPreviewEditButton__phoneNumber: 'Edit phone number',
  impersonationFab: {
    action__signOut: 'Sign out',
    title: 'Signed in as {{identifier}}',
  },
  lastAuthenticationStrategy: 'Last used',
  maintenanceMode:
    "We are currently undergoing maintenance, but don't worry, it shouldn't take more than a few minutes.",
  membershipRole__admin: 'Admin',
  membershipRole__basicMember: 'Member',
  membershipRole__guestMember: 'Guest',
  oauthConsent: {
    action__allow: 'Allow',
    action__deny: 'Deny',
    offlineAccessNotice: " You'll stay signed in until you sign out or revoke access.",
    redirectNotice: 'If you allow access, this app will redirect you to {{domainAction}}.',
    redirectUriModal: {
      subtitle: 'Make sure you trust {{applicationName}} and that this URL belongs to {{applicationName}}.',
      title: 'Redirect URL',
    },
    scopeList: {
      title: 'This will allow {{applicationName}} access to:',
    },
    subtitle: 'wants to access {{applicationName}} on behalf of {{identifier}}',
    viewFullUrl: 'View full URL',
    warning:
      'Make sure that you trust {{applicationName}} ({{domainAction}}). You may be sharing sensitive data with this site or app.',
  },
  organizationList: {
    action__createOrganization: 'Create organization',
    action__invitationAccept: 'Join',
    action__suggestionsAccept: 'Request to join',
    createOrganization: 'Create Organization',
    invitationAcceptedLabel: 'Joined',
    subtitle: 'to continue to {{applicationName}}',
    suggestionsAcceptedLabel: 'Pending approval',
    title: 'Choose an account',
    titleWithoutPersonal: 'Choose an organization',
  },
  organizationProfile: {
    apiKeysPage: {
      title: 'API keys',
    },
    badge__automaticInvitation: 'Automatic invitations',
    badge__automaticSuggestion: 'Automatic suggestions',
    badge__enterpriseSso: 'Enterprise SSO',
    badge__manualInvitation: 'No automatic enrollment',
    badge__unverified: 'Unverified',
    billingPage: {
      accountCreditsSection: {
        title: 'Account credits',
        viewHistory: 'View credit history',
      },
      creditHistoryPage: {
        tableHeader__amount: 'Amount',
        tableHeader__date: 'Date',
        title: 'Account credit history',
      },
      paymentHistorySection: {
        empty: 'No payment history',
        notFound: 'Payment attempt not found',
        tableHeader__amount: 'Amount',
        tableHeader__date: 'Date',
        tableHeader__status: 'Status',
      },
      paymentMethodsSection: {
        actionLabel__default: 'Make default',
        actionLabel__remove: 'Remove',
        add: 'Add new payment method',
        addSubtitle: 'Add a new payment method to your account.',
        cancelButton: 'Cancel',
        formButtonPrimary__add: 'Add Payment Method',
        formButtonPrimary__pay: 'Pay {{amount}}',
        payWithTestCardButton: 'Pay with test card',
        removeMethod: {
          messageLine1: '{{identifier}} will be removed from this account.',
          messageLine2:
            'You will no longer be able to use this payment method and any recurring subscriptions dependent on it will no longer work.',
          successMessage: '{{paymentMethod}} has been removed from your account.',
          title: 'Remove payment method',
        },
        title: 'Payment methods',
      },
      start: {
        headerTitle__payments: 'Payments',
        headerTitle__plans: 'Plans',
        headerTitle__statements: 'Statements',
        headerTitle__subscriptions: 'Subscription',
      },
      statementsSection: {
        empty: 'No statements to display',
        itemCaption__paidForPlan: 'Paid for {{plan}} {{period}} plan',
        itemCaption__payerCredit: 'Credit from account balance',
        itemCaption__proratedCredit: 'Prorated credit for partial usage of previous subscription',
        itemCaption__subscribedAndPaidForPlan: 'Subscribed and paid for {{plan}} {{period}} plan',
        notFound: 'Statement not found',
        tableHeader__amount: 'Amount',
        tableHeader__date: 'Date',
        title: 'Statements',
        totalPaid: 'Total paid',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: 'Manage',
        actionLabel__newSubscription: 'Subscribe to a plan',
        actionLabel__switchPlan: 'Switch plans',
        includedSeatsUsage: '{{includedSeats}} seats included',
        overview: 'Overview',
        paidSeatsUsage: '{{seatsQuantity}} seats x {{amount}}',
        seatLimit: 'Up to {{seatLimit}} seats',
        seatLimitAndIncludedSeats: 'Up to {{seatLimit}} seats ({{includedSeats}} included)',
        tableHeader__edit: 'Edit',
        tableHeader__plan: 'Plan',
        tableHeader__startDate: 'Start date',
        title: 'Subscription',
      },
      subscriptionsSection: {
        actionLabel__default: 'Manage',
      },
      switchPlansSection: {
        title: 'Switch plans',
      },
      title: 'Billing',
    },
    createDomainPage: {
      subtitle:
        'Add the domain to verify. Users with email addresses at this domain can join the organization automatically or request to join.',
      title: 'Add domain',
    },
    invitePage: {
      detailsTitle__inviteFailed:
        'The invitations could not be sent. There are already pending invitations for the following email addresses: {{email_addresses}}.',
      formButtonPrimary__continue: 'Send invitations',
      formButtonPrimary__purchaseSeats: 'Purchase additional seats',
      selectDropdown__role: 'Select role',
      subtitle: 'Enter or paste one or more email addresses, separated by spaces or commas.',
      successMessage: 'Invitations successfully sent',
      title: 'Invite new members',
    },
    membersPage: {
      action__invite: 'Invite',
      action__search: 'Search',
      activeMembersTab: {
        menuAction__remove: 'Remove member',
        tableHeader__actions: 'Actions',
        tableHeader__joined: 'Joined',
        tableHeader__role: 'Role',
        tableHeader__user: 'User',
      },
      alerts: {
        roleSetMigrationInProgress: {
          subtitle: 'We are updating the available roles. Once that’s done, you’ll be able to update roles again.',
          title: 'Roles are temporarily locked',
        },
      },
      detailsTitle__emptyRow: 'No members to display',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            'Invite users by connecting an email domain with your organization. Anyone who signs up with a matching email domain will be able to join the organization anytime.',
          headerTitle: 'Automatic invitations',
          primaryButton: 'Manage verified domains',
        },
        table__emptyRow: 'No invitations to display',
      },
      invitedMembersTab: {
        menuAction__revoke: 'Revoke invitation',
        tableHeader__invited: 'Invited',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle:
            'Users who sign up with a matching email domain, will be able to see a suggestion to request to join your organization.',
          headerTitle: 'Automatic suggestions',
          primaryButton: 'Manage verified domains',
        },
        menuAction__approve: 'Approve',
        menuAction__reject: 'Reject',
        tableHeader__requested: 'Requested access',
        table__emptyRow: 'No requests to display',
      },
      start: {
        headerTitle__invitations: 'Invitations',
        headerTitle__members: 'Members',
        headerTitle__requests: 'Requests',
      },
    },
    navbar: {
      apiKeys: 'API keys',
      billing: 'Billing',
      description: 'Manage your organization.',
      general: 'General',
      members: 'Members',
      security: 'Security',
      title: 'Organization',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: 'You do not have permissions to manage billing for this organization.',
        planMembershipLimitExceeded:
          'Your organization has {{count}} members (including pending invitations). This plan only allows {{limit}} members.',
      },
      title: 'Plans',
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: 'Type "{{organizationName}}" below to continue.',
          messageLine1: 'Are you sure you want to delete this organization?',
          messageLine2: 'This action is permanent and irreversible.',
          successMessage: 'You have deleted the organization.',
          title: 'Delete organization',
        },
        leaveOrganization: {
          actionDescription: 'Type "{{organizationName}}" below to continue.',
          messageLine1:
            'Are you sure you want to leave this organization? You will lose access to this organization and its applications.',
          messageLine2: 'This action is permanent and irreversible.',
          successMessage: 'You have left the organization.',
          title: 'Leave organization',
        },
        title: 'Danger',
      },
      domainSection: {
        menuAction__manage: 'Manage',
        menuAction__remove: 'Delete',
        menuAction__verify: 'Verify',
        primaryButton: 'Add domain',
        subtitle:
          'Allow users to join the organization automatically or request to join based on a verified email domain.',
        title: 'Verified domains',
      },
      successMessage: 'The organization has been updated.',
      title: 'Update profile',
    },
    removeDomainPage: {
      messageLine1: 'The email domain {{domain}} will be removed.',
      messageLine2: 'Users won’t be able to join the organization automatically after this.',
      successMessage: '{{domain}} has been removed.',
      title: 'Remove domain',
    },
    securityPage: {
      removeDialog: {
        confirmButton: 'Remove connection',
        subtitle:
          'Are you sure you want to remove the connection? This action is irreversible and deletes the connection and all of its configuration.',
        title: 'Remove SSO connection',
      },
      ssoSection: {
        badge__active: 'Active',
        badge__inProgress: 'In Progress',
        badge__inactive: 'Inactive',
        badge__unconfigured: 'Unconfigured',
        descriptionLine1: 'Require members with a matching email domain to sign in through your identity provider.',
        domainLabel: 'Domains:',
        menuAction__activate: 'Activate',
        menuAction__deactivate: 'Deactivate',
        menuAction__edit: 'Edit',
        menuAction__remove: 'Remove',
        primaryButton__continueConfiguration: 'Continue configuration',
        primaryButton__startConfiguration: 'Start configuration',
        title: 'SSO',
        tooltip:
          'Members without a matching domain can still sign in using existing auth methods. New members will be assigned to {{role}} in this organization.',
        tooltipLabel: 'More information',
        tooltip__noRole: 'Members without a matching domain can still sign in using existing auth methods.',
      },
      title: 'Security',
    },
    start: {
      headerTitle__general: 'General',
      headerTitle__members: 'Members',
      membershipSeatUsageLabel: '{{count}} of {{limit}} seats used',
      profileSection: {
        primaryButton: 'Update profile',
        title: 'Profile',
        uploadAction__title: 'Logo',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: 'Removing this domain will affect invited users.',
        removeDomainActionLabel__remove: 'Remove domain',
        removeDomainSubtitle: 'Remove this domain from your verified domains',
        removeDomainTitle: 'Remove domain',
      },
      enrollmentTab: {
        automaticInvitationOption__description:
          'Users are automatically invited to join the organization when they sign-up and can join anytime.',
        automaticInvitationOption__label: 'Automatic invitations',
        automaticSuggestionOption__description:
          'Users receive a suggestion to request to join, but must be approved by an admin before they are able to join the organization.',
        automaticSuggestionOption__label: 'Automatic suggestions',
        calloutInfoLabel: 'Changing the enrollment mode will only affect new users.',
        calloutInvitationCountLabel: 'Pending invitations sent to users: {{count}}',
        calloutSuggestionCountLabel: 'Pending suggestions sent to users: {{count}}',
        manualInvitationOption__description: 'Users can only be invited manually to the organization.',
        manualInvitationOption__label: 'No automatic enrollment',
        subtitle: 'Choose how users from this domain can join the organization.',
      },
      start: {
        headerTitle__danger: 'Danger',
        headerTitle__enrollment: 'Enrollment options',
      },
      subtitle: 'The domain {{domain}} is now verified. Continue by selecting enrollment mode.',
      title: 'Update {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: 'Enter the verification code sent to your email address',
      formTitle: 'Verification code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'The domain {{domainName}} needs to be verified via email.',
      subtitleVerificationCodeScreen: 'A verification code was sent to {{emailAddress}}. Enter the code to continue.',
      title: 'Verify domain',
    },
  },
  organizationSwitcher: {
    action__closeOrganizationSwitcher: 'Close organization switcher',
    action__createOrganization: 'Create organization',
    action__invitationAccept: 'Join',
    action__manageOrganization: 'Manage',
    action__openOrganizationSwitcher: 'Open organization switcher',
    action__suggestionsAccept: 'Request to join',
    notSelected: 'No organization selected',
    personalWorkspace: 'Personal account',
    suggestionsAcceptedLabel: 'Pending approval',
  },
  paginationButton__next: 'Next',
  paginationButton__previous: 'Previous',
  paginationRowText__displaying: 'Displaying',
  paginationRowText__of: 'of',
  reverification: {
    alternativeMethods: {
      actionLink: 'Get help',
      actionText: 'Don’t have any of these?',
      blockButton__backupCode: 'Use a backup code',
      blockButton__emailCode: 'Email code to {{identifier}}',
      blockButton__passkey: 'Use your passkey',
      blockButton__password: 'Continue with your password',
      blockButton__phoneCode: 'Send SMS code to {{identifier}}',
      blockButton__totp: 'Use your authenticator app',
      getHelp: {
        blockButton__emailSupport: 'Email support',
        content:
          'If you have trouble verifying your account, email us and we will work with you to restore access as soon as possible.',
        title: 'Get help',
      },
      subtitle: 'Facing issues? You can use any of these methods for verification.',
      title: 'Use another method',
    },
    backupCodeMfa: {
      subtitle: 'Enter the backup code you received when setting up two-step authentication',
      title: 'Enter a backup code',
    },
    emailCode: {
      formTitle: 'Verification code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'Enter the code sent to your email to continue',
      title: 'Verification required',
    },
    noAvailableMethods: {
      message: 'Cannot proceed with verification. No suitable authentication factor is configured',
      subtitle: 'An error occurred',
      title: 'Cannot verify your account',
    },
    passkey: {
      blockButton__passkey: 'Use your passkey',
      subtitle:
        'Using your passkey confirms your identity. Your device may ask for your fingerprint, face, or screen lock.',
      title: 'Use your passkey',
    },
    password: {
      actionLink: 'Use another method',
      subtitle: 'Enter your current password to continue',
      title: 'Verification required',
    },
    phoneCode: {
      formTitle: 'Verification code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'Enter the code sent to your phone to continue',
      title: 'Verification required',
    },
    phoneCodeMfa: {
      formTitle: 'Verification code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'Enter the code sent to your phone to continue',
      title: 'Verification required',
    },
    totpMfa: {
      formTitle: 'Verification code',
      subtitle: 'Enter the code generated by your authenticator app to continue',
      title: 'Verification required',
    },
  },
  searchInput: {
    action__clear: 'Clear search',
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: 'Add account',
      action__signOutAll: 'Sign out of all accounts',
      subtitle: 'Select the account with which you wish to continue.',
      title: 'Choose an account',
    },
    alternativeMethods: {
      actionLink: 'Get help',
      actionText: 'Don’t have any of these?',
      blockButton__backupCode: 'Use a backup code',
      blockButton__emailCode: 'Email code to {{identifier}}',
      blockButton__emailLink: 'Email link to {{identifier}}',
      blockButton__passkey: 'Sign in with your passkey',
      blockButton__password: 'Sign in with your password',
      blockButton__phoneCode: 'Send SMS code to {{identifier}}',
      blockButton__totp: 'Use your authenticator app',
      getHelp: {
        blockButton__emailSupport: 'Email support',
        content:
          'If you have trouble signing into your account, email us and we will work with you to restore access as soon as possible.',
        title: 'Get help',
      },
      subtitle: 'Facing issues? You can use any of these methods to sign in.',
      title: 'Use another method',
    },
    alternativePhoneCodeProvider: {
      formTitle: 'Verification code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'to continue to {{applicationName}}',
      title: 'Check your {{provider}}',
    },
    backupCodeMfa: {
      subtitle: 'Your backup code is the one you got when setting up two-step authentication.',
      title: 'Enter a backup code',
    },
    emailCode: {
      formTitle: 'Verification code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'to continue to {{applicationName}}',
      title: 'Check your email',
    },
    emailCodeMfa: {
      formTitle: 'Check your email',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'to continue to {{applicationName}}',
      title: 'Check your email',
    },
    emailLink: {
      clientMismatch: {
        subtitle:
          'To continue, open the verification link on the device and browser from which you initiated the sign-in',
        title: 'Verification link is invalid for this device',
      },
      expired: {
        subtitle: 'Return to the original tab to continue.',
        title: 'This verification link has expired',
      },
      failed: {
        subtitle: 'Return to the original tab to continue.',
        title: 'This verification link is invalid',
      },
      formSubtitle: 'Use the verification link sent to your email',
      formTitle: 'Verification link',
      loading: {
        subtitle: 'You will be redirected soon',
        title: 'Signing in...',
      },
      resendButton: "Didn't receive a link? Resend",
      subtitle: 'to continue to {{applicationName}}',
      title: 'Check your email',
      unusedTab: {
        title: 'You may close this tab',
      },
      verified: {
        subtitle: 'You will be redirected soon',
        title: 'Successfully signed in',
      },
      verifiedSwitchTab: {
        subtitle: 'Return to original tab to continue',
        subtitleNewTab: 'Return to the newly opened tab to continue',
        titleNewTab: 'Signed in on other tab',
      },
    },
    emailLinkMfa: {
      formSubtitle: 'Use the verification link sent to your email',
      resendButton: "Didn't receive a link? Resend",
      subtitle: 'to continue to {{applicationName}}',
      title: 'Check your email',
    },
    enterpriseConnections: {
      subtitle: 'Select the enterprise account with which you wish to continue.',
      title: 'Choose your enterprise account',
    },
    forgotPassword: {
      formTitle: 'Reset password code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'to reset your password',
      subtitle_email: 'First, enter the code sent to your email address',
      subtitle_phone: 'First, enter the code sent to your phone',
      title: 'Reset password',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: 'Reset your password',
      label__alternativeMethods: 'Or, sign in with another method',
      title: 'Forgot Password?',
    },
    newDeviceVerificationNotice:
      "You're signing in from a new device. We're asking for verification to keep your account secure.",
    noAvailableMethods: {
      message: "Cannot proceed with sign in. There's no available authentication factor.",
      subtitle: 'An error occurred',
      title: 'Cannot sign in',
    },
    passkey: {
      subtitle: "Using your passkey confirms it's you. Your device may ask for your fingerprint, face or screen lock.",
      title: 'Use your passkey',
    },
    password: {
      actionLink: 'Use another method',
      subtitle: 'Enter the password associated with your account',
      title: 'Enter your password',
    },
    passwordCompromised: {
      title: 'Password compromised',
    },
    passwordPwned: {
      title: 'Password compromised',
    },
    passwordUntrusted: {
      title: 'Password untrusted',
    },
    phoneCode: {
      formTitle: 'Verification code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'to continue to {{applicationName}}',
      title: 'Check your phone',
    },
    phoneCodeMfa: {
      formTitle: 'Verification code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'To continue, please enter the verification code sent to your phone',
      title: 'Check your phone',
    },
    protectCheck: {
      loading: 'Loading…',
      retryButton: 'Try again',
      subtitle: 'Please wait while we verify your request.',
      title: 'Verifying your request',
    },
    resetPassword: {
      formButtonPrimary: 'Reset Password',
      requiredMessage: 'For security reasons, it is required to reset your password.',
      successMessage: 'Your password was successfully changed. Signing you in, please wait a moment.',
      title: 'Set new password',
    },
    resetPasswordMfa: {
      detailsLabel: 'We need to verify your identity before resetting your password.',
    },
    start: {
      actionLink: 'Sign up',
      actionLink__join_waitlist: 'Join waitlist',
      actionLink__use_email: 'Use email',
      actionLink__use_email_username: 'Use email or username',
      actionLink__use_passkey: 'Use passkey instead',
      actionLink__use_phone: 'Use phone',
      actionLink__use_username: 'Use username',
      actionText: 'Don’t have an account?',
      actionText__join_waitlist: 'Want early access?',
      alternativePhoneCodeProvider: {
        actionLink: 'Use another method',
        label: '{{provider}} phone number',
        subtitle: 'Enter your phone number to get a verification code on {{provider}}.',
        title: 'Sign in to {{applicationName}} with {{provider}}',
      },
      subtitle: 'Welcome back! Please sign in to continue',
      subtitleCombined: undefined,
      title: 'Sign in to {{applicationName}}',
      titleCombined: 'Continue to {{applicationName}}',
    },
    totpMfa: {
      formTitle: 'Verification code',
      subtitle: 'To continue, please enter the verification code generated by your authenticator app',
      title: 'Two-step verification',
    },
    web3Solana: {
      subtitle: 'Select a wallet below to sign in',
      title: 'Sign in with Solana',
    },
  },
  signInEnterPasswordTitle: 'Enter your password',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'Enter the verification code sent to your {{provider}}',
      title: 'Verify your {{provider}}',
    },
    continue: {
      actionLink: 'Sign in',
      actionText: 'Already have an account?',
      subtitle: 'Please fill in the remaining details to continue.',
      title: 'Fill in missing fields',
    },
    emailCode: {
      formSubtitle: 'Enter the verification code sent to your email address',
      formTitle: 'Verification code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'Enter the verification code sent to your email',
      title: 'Verify your email',
    },
    emailLink: {
      clientMismatch: {
        subtitle:
          'To continue, open the verification link on the device and browser from which you initiated the sign-up',
        title: 'Verification link is invalid for this device',
      },
      formSubtitle: 'Use the verification link sent to your email address',
      formTitle: 'Verification link',
      loading: {
        title: 'Signing up...',
      },
      resendButton: "Didn't receive a link? Resend",
      subtitle: 'to continue to {{applicationName}}',
      title: 'Verify your email',
      verified: {
        title: 'Successfully signed up',
      },
      verifiedSwitchTab: {
        subtitle: 'Return to the newly opened tab to continue',
        subtitleNewTab: 'Return to previous tab to continue',
        title: 'Successfully verified email',
      },
    },
    enterpriseConnections: {
      subtitle: 'Select the enterprise account with which you wish to continue.',
      title: 'Choose your enterprise account',
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: 'I agree to the {{ privacyPolicyLink || link("Privacy Policy") }}',
        label__onlyTermsOfService: 'I agree to the {{ termsOfServiceLink || link("Terms of Service") }}',
        label__termsOfServiceAndPrivacyPolicy:
          'I agree to the {{ termsOfServiceLink || link("Terms of Service") }} and {{ privacyPolicyLink || link("Privacy Policy") }}',
      },
      continue: {
        subtitle: 'Please read and accept the terms to continue',
        title: 'Legal consent',
      },
    },
    phoneCode: {
      formSubtitle: 'Enter the verification code sent to your phone number',
      formTitle: 'Verification code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'Enter the verification code sent to your phone',
      title: 'Verify your phone',
    },
    protectCheck: {
      loading: 'Loading…',
      retryButton: 'Try again',
      subtitle: 'Please wait while we verify your request.',
      title: 'Verifying your request',
    },
    restrictedAccess: {
      actionLink: 'Sign in',
      actionText: 'Already have an account?',
      blockButton__emailSupport: 'Email support',
      blockButton__joinWaitlist: 'Join waitlist',
      subtitle: 'Sign ups are currently disabled. If you believe you should have access, please contact support.',
      subtitleWaitlist: 'Sign ups are currently disabled. To be the first to know when we launch, join the waitlist.',
      title: 'Access restricted',
    },
    start: {
      actionLink: 'Sign in',
      actionLink__use_email: 'Use email instead',
      actionLink__use_phone: 'Use phone instead',
      actionText: 'Already have an account?',
      alternativePhoneCodeProvider: {
        actionLink: 'Use another method',
        label: '{{provider}} phone number',
        subtitle: 'Enter your phone number to get a verification code on {{provider}}.',
        title: 'Sign up to {{applicationName}} with {{provider}}',
      },
      subtitle: 'Welcome! Please fill in the details to get started.',
      subtitleCombined: 'Welcome! Please fill in the details to get started.',
      title: 'Create your account',
      titleCombined: 'Create your account',
    },
    web3Solana: {
      subtitle: 'Select a wallet below to sign up',
      title: 'Sign up with Solana',
    },
  },
  socialButtonsBlockButton: 'Continue with {{provider|titleize}}',
  socialButtonsBlockButtonManyInView: '{{provider|titleize}}',
  taskChooseOrganization: {
    alerts: {
      organizationAlreadyExists:
        'An organization already exists for the detected company name ({{organizationName}}) and {{organizationDomain}}. Join by invitation.',
    },
    chooseOrganization: {
      action__createOrganization: 'Create new organization',
      action__invitationAccept: 'Join',
      action__suggestionsAccept: 'Request to join',
      subtitle: 'Join an existing organization or create a new one',
      subtitle__createOrganizationDisabled: 'Join an existing organization',
      suggestionsAcceptedLabel: 'Pending approval',
      title: 'Choose an organization',
    },
    createOrganization: {
      formButtonReset: 'Cancel',
      formButtonSubmit: 'Continue',
      formFieldInputPlaceholder__name: 'My Organization',
      formFieldInputPlaceholder__slug: 'my-organization',
      formFieldLabel__name: 'Name',
      formFieldLabel__slug: 'Slug',
      subtitle: 'Enter your organization details to continue',
      title: 'Setup your organization',
    },
    organizationCreationDisabled: {
      subtitle: 'Contact your organization admin for an invitation.',
      title: 'You must belong to an organization',
    },
    signOut: {
      actionLink: 'Sign out',
      actionText: 'Signed in as {{identifier}}',
    },
  },
  taskResetPassword: {
    formButtonPrimary: 'Reset Password',
    signOut: {
      actionLink: 'Sign out',
      actionText: 'Signed in as {{identifier}}',
    },
    subtitle: 'Your account requires a new password before you can continue',
    title: 'Reset your password',
  },
  taskSetupMfa: {
    badge: 'Two-step verification setup',
    signOut: {
      actionLink: 'Sign out',
      actionText: 'Signed in as {{identifier}}',
    },
    smsCode: {
      addPhone: {
        formButtonPrimary: 'Continue',
        infoText:
          'A text message containing a verification code will be sent to this phone number. Message and data rates may apply.',
      },
      addPhoneNumber: 'Add phone number',
      cancel: 'Cancel',
      subtitle: 'Choose phone number you want to use for SMS code two-step verification',
      success: {
        finishButton: 'Continue',
        message1:
          'Two-step verification is now enabled. When signing in, you will need to enter a verification code sent to this phone number as an additional step.',
        message2:
          'Save these backup codes and store them somewhere safe. If you lose access to your authentication device, you can use backup codes to sign in.',
        title: 'SMS code verification enabled',
      },
      title: 'Add SMS code verification',
      verifyPhone: {
        formButtonPrimary: 'Continue',
        formTitle: 'Verification code',
        resendButton: "Didn't receive a code? Resend",
        subtitle: 'Enter the verification code sent to',
        title: 'Verify your phone number',
      },
    },
    start: {
      methodSelection: {
        phoneCode: 'SMS code',
        totp: 'Authenticator application',
      },
      subtitle: 'Choose which method you prefer to protect your account with an extra layer of security',
      title: 'Set up two-step verification',
    },
    totpCode: {
      addAuthenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Scan QR code instead',
        buttonUnableToScan__nonPrimary: "Can't scan QR code?",
        formButtonPrimary: 'Continue',
        formButtonReset: 'Cancel',
        infoText__ableToScan:
          'Set up a new sign-in method in your authenticator app and scan the following QR code to link it to your account.',
        infoText__unableToScan: 'Set up a new sign-in method in your authenticator and enter the Key provided below.',
        inputLabel__unableToScan1:
          'Make sure Time-based or One-time passwords is enabled, then finish linking your account.',
      },
      success: {
        finishButton: 'Continue',
        message1:
          'Two-step verification is now enabled. When signing in, you will need to enter a verification code from this authenticator as an additional step.',
        message2:
          'Save these backup codes and store them somewhere safe. If you lose access to your authentication device, you can use backup codes to sign in.',
        title: 'Authenticator application verification enabled',
      },
      title: 'Add authenticator application',
      verifyTotp: {
        formButtonPrimary: 'Continue',
        formButtonReset: 'Cancel',
        formTitle: 'Verification code',
        subtitle: 'Enter verification code generated by your authenticator',
        title: 'Add authenticator application',
      },
    },
  },
  unstable__errors: {
    action_blocked: "This action couldn't be completed. Please try again later or contact support if this persists.",
    already_a_member_in_organization: '{{email}} is already a member of the organization.',
    api_key_name_already_exists: 'API Key name already exists.',
    api_key_usage_exceeded: 'You have reached your usage limit. You can remove the limit by upgrading to a paid plan.',
    avatar_file_size_exceeded: 'File size exceeds the maximum limit of 10MB. Please choose a smaller file.',
    avatar_file_type_invalid: 'File type not supported. Please upload a JPG, PNG, GIF, or WEBP image.',
    captcha_invalid: undefined,
    captcha_unavailable:
      'Sign up unsuccessful due to failed bot validation. Please refresh the page to try again or reach out to support for more assistance.',
    form_code_incorrect: undefined,
    form_email_address_blocked: undefined,
    form_identifier_exists__email_address: undefined,
    form_identifier_exists__phone_number: undefined,
    form_identifier_exists__username: undefined,
    form_identifier_not_found: undefined,
    form_new_password_matches_current: 'New password cannot be the same as the current password.',
    form_param_format_invalid: undefined,
    form_param_format_invalid__email_address: undefined,
    form_param_format_invalid__phone_number: undefined,
    form_param_max_length_exceeded__first_name: undefined,
    form_param_max_length_exceeded__last_name: undefined,
    form_param_max_length_exceeded__name: undefined,
    form_param_nil: undefined,
    form_param_type_invalid: undefined,
    form_param_type_invalid__email_address: undefined,
    form_param_type_invalid__phone_number: undefined,
    form_param_value_invalid: undefined,
    form_password_compromised__sign_in: undefined,
    form_password_incorrect: undefined,
    form_password_length_too_short: 'Your password is too short. It must be at least 8 characters long.',
    form_password_not_strong_enough: 'Your password is not strong enough.',
    form_password_or_identifier_incorrect: undefined,
    form_password_pwned:
      'This password has been found as part of a breach and can not be used, please try another password instead.',
    form_password_pwned__sign_in:
      'This password has been found as part of a breach and can not be used, please reset your password.',
    form_password_size_in_bytes_exceeded: undefined,
    form_password_untrusted__sign_in:
      'Your password may be compromised. To protect your account, please continue with an alternative sign-in method. You will be required to reset your password after signing in.',
    form_password_validation_failed: undefined,
    form_username_invalid_character: undefined,
    form_username_invalid_length: 'Your username must be between {{min_length}} and {{max_length}} characters long.',
    form_username_needs_non_number_char: 'Your username must contain at least one non-numeric character.',
    identification_deletion_failed: undefined,
    insufficient_seats_change_plan:
      'Your organization does not have enough seats to invite the desired number of members. Please change to a plan that supports the number of members you are attempting to invite.',
    insufficient_seats_contact_support:
      'Your organization does not have enough seats to invite the desired number of members. Please contact support.',
    not_allowed_access: undefined,
    oauth_access_denied: 'You did not grant access to your account.',
    organization_domain_blocked: undefined,
    organization_domain_common: undefined,
    organization_domain_exists_for_enterprise_connection: undefined,
    organization_membership_quota_exceeded:
      'You have reached your limit of organization memberships, including outstanding invitations.',
    organization_minimum_permissions_needed: undefined,
    organization_not_found_or_unauthorized:
      'You are no longer a member of this organization. Please choose or create another one.',
    organization_not_found_or_unauthorized_with_create_organization_disabled:
      'You are no longer a member of this organization. Please choose another one.',
    passkey_already_exists: 'A passkey is already registered with this device.',
    passkey_not_supported: 'Passkeys are not supported on this device.',
    passkey_pa_not_supported: 'Registration requires a platform authenticator but the device does not support it.',
    passkey_registration_cancelled: 'Passkey registration was cancelled or timed out.',
    passkey_retrieval_cancelled: 'Passkey verification was cancelled or timed out.',
    passwordComplexity: {
      maximumLength: 'less than {{length}} characters',
      minimumLength: '{{length}} or more characters',
      requireLowercase: 'a lowercase letter',
      requireNumbers: 'a number',
      requireSpecialCharacter: 'a special character',
      requireUppercase: 'an uppercase letter',
      sentencePrefix: 'Your password must contain',
    },
    phone_number_exists: undefined,
    protect_check_aborted: undefined,
    protect_check_already_resolved: undefined,
    protect_check_execution_failed: "Verification didn't complete. Please try again.",
    protect_check_invalid_script: "Couldn't load verification. Please contact support if this persists.",
    protect_check_invalid_sdk_url: "Verification couldn't start. Please contact support.",
    protect_check_script_load_failed:
      "Couldn't load verification. This may be caused by a network issue or a Content Security Policy that blocks the verification script. Please try again or contact support.",
    protect_check_timed_out: "Verification didn't complete in time. Please try again.",
    protect_check_unsupported_environment:
      "Verification isn't supported in this environment. Please continue in a standard browser or contact support.",
    session_exists: undefined,
    web3_missing_identifier: 'A Web3 Wallet extension cannot be found. Please install one to continue.',
    web3_signature_request_rejected: 'You have rejected the signature request. Please try again to continue.',
    web3_solana_signature_generation_failed:
      'An error occurred while generating the signature. Please try again to continue.',
    zxcvbn: {
      couldBeStronger: 'Your password works, but could be stronger. Try adding more characters.',
      goodPassword: 'Your password meets all the necessary requirements.',
      notEnough: 'Your password is not strong enough.',
      suggestions: {
        allUppercase: 'Capitalize some, but not all letters.',
        anotherWord: 'Add more words that are less common.',
        associatedYears: 'Avoid years that are associated with you.',
        capitalization: 'Capitalize more than the first letter.',
        dates: 'Avoid dates and years that are associated with you.',
        l33t: "Avoid predictable letter substitutions like '@' for 'a'.",
        longerKeyboardPattern: 'Use longer keyboard patterns and change typing direction multiple times.',
        noNeed: 'You can create strong passwords without using symbols, numbers, or uppercase letters.',
        pwned: 'If you use this password elsewhere, you should change it.',
        recentYears: 'Avoid recent years.',
        repeated: 'Avoid repeated words and characters.',
        reverseWords: 'Avoid reversed spellings of common words.',
        sequences: 'Avoid common character sequences.',
        useWords: 'Use multiple words, but avoid common phrases.',
      },
      warnings: {
        common: 'This is a commonly used password.',
        commonNames: 'Common names and surnames are easy to guess.',
        dates: 'Dates are easy to guess.',
        extendedRepeat: 'Repeated character patterns like "abcabcabc" are easy to guess.',
        keyPattern: 'Short keyboard patterns are easy to guess.',
        namesByThemselves: 'Single names or surnames are easy to guess.',
        pwned: 'Your password was exposed by a data breach on the Internet.',
        recentYears: 'Recent years are easy to guess.',
        sequences: 'Common character sequences like "abc" are easy to guess.',
        similarToCommon: 'This is similar to a commonly used password.',
        simpleRepeat: 'Repeated characters like "aaa" are easy to guess.',
        straightRow: 'Straight rows of keys on your keyboard are easy to guess.',
        topHundred: 'This is a frequently used password.',
        topTen: 'This is a heavily used password.',
        userInputs: 'There should not be any personal or page related data.',
        wordByItself: 'Single words are easy to guess.',
      },
    },
  },
  userButton: {
    action__addAccount: 'Add account',
    action__closeUserMenu: 'Close user menu',
    action__manageAccount: 'Manage account',
    action__openUserMenu: 'Open user menu',
    action__signOut: 'Sign out',
    action__signOutAll: 'Sign out of all accounts',
    label__accountActions: 'Account actions',
    label__activeSessions: 'Active sessions',
    label__userButtonPopover: 'Account panel',
  },
  userProfile: {
    apiKeysPage: {
      title: 'API keys',
    },
    backupCodePage: {
      actionLabel__copied: 'Copied!',
      actionLabel__copy: 'Copy all',
      actionLabel__download: 'Download .txt',
      actionLabel__print: 'Print',
      infoText1: 'Backup codes will be enabled for this account.',
      infoText2:
        'Keep the backup codes secret and store them securely. You may regenerate backup codes if you suspect they have been compromised.',
      subtitle__codelist: 'Store them securely and keep them secret.',
      successMessage:
        'Backup codes are now enabled. You can use one of these to sign in to your account, if you lose access to your authentication device. Each code can only be used once.',
      successSubtitle:
        'You can use one of these to sign in to your account, if you lose access to your authentication device.',
      title: 'Add backup code verification',
      title__codelist: 'Backup codes',
    },
    billingPage: {
      accountCreditsSection: {
        title: 'Account credits',
        viewHistory: 'View credit history',
      },
      creditHistoryPage: {
        tableHeader__amount: 'Amount',
        tableHeader__date: 'Date',
        title: 'Account credit history',
      },
      paymentHistorySection: {
        empty: 'No payment history',
        notFound: 'Payment attempt not found',
        tableHeader__amount: 'Amount',
        tableHeader__date: 'Date',
        tableHeader__status: 'Status',
      },
      paymentMethodsSection: {
        actionLabel__default: 'Make default',
        actionLabel__remove: 'Remove',
        add: 'Add new payment method',
        addSubtitle: 'Add a new payment method to your account.',
        cancelButton: 'Cancel',
        formButtonPrimary__add: 'Add Payment Method',
        formButtonPrimary__pay: 'Pay {{amount}}',
        payWithTestCardButton: 'Pay with test card',
        removeMethod: {
          messageLine1: '{{identifier}} will be removed from this account.',
          messageLine2:
            'You will no longer be able to use this payment source and any recurring subscriptions dependent on it will no longer work.',
          successMessage: '{{paymentMethod}} has been removed from your account.',
          title: 'Remove payment method',
        },
        title: 'Payment methods',
      },
      start: {
        headerTitle__payments: 'Payments',
        headerTitle__plans: 'Plans',
        headerTitle__statements: 'Statements',
        headerTitle__subscriptions: 'Subscription',
      },
      statementsSection: {
        empty: 'No statements to display',
        itemCaption__paidForPlan: 'Paid for {{plan}} {{period}} plan',
        itemCaption__payerCredit: 'Credit from account balance',
        itemCaption__proratedCredit: 'Prorated credit for partial usage of previous subscription',
        itemCaption__subscribedAndPaidForPlan: 'Subscribed and paid for {{plan}} {{period}} plan',
        notFound: 'Statement not found',
        tableHeader__amount: 'Amount',
        tableHeader__date: 'Date',
        title: 'Statements',
        totalPaid: 'Total paid',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: 'Manage',
        actionLabel__newSubscription: 'Subscribe to a plan',
        actionLabel__switchPlan: 'Switch plans',
        overview: 'Overview',
        tableHeader__edit: 'Edit',
        tableHeader__plan: 'Plan',
        tableHeader__startDate: 'Start date',
        title: 'Subscription',
      },
      subscriptionsSection: {
        actionLabel__default: 'Manage',
      },
      switchPlansSection: {
        title: 'Switch plans',
      },
      title: 'Billing',
    },
    connectedAccountPage: {
      formHint: 'Select a provider to connect your account.',
      formHint__noAccounts: 'There are no available external account providers.',
      removeResource: {
        messageLine1: '{{identifier}} will be removed from this account.',
        messageLine2:
          'You will no longer be able to use this connected account and any dependent features will no longer work.',
        successMessage: '{{connectedAccount}} has been removed from your account.',
        title: 'Remove connected account',
      },
      socialButtonsBlockButton: '{{provider|titleize}}',
      successMessage: 'The provider has been added to your account',
      title: 'Add connected account',
    },
    deletePage: {
      actionDescription: 'Type "Delete account" below to continue.',
      confirm: 'Delete account',
      messageLine1:
        'Are you sure you want to delete your account? Some associated data may be retained. To request full data deletion, please contact support.',
      messageLine2: 'This action is permanent and irreversible.',
      title: 'Delete account',
    },
    emailAddressPage: {
      emailCode: {
        formHint: 'An email containing a verification code will be sent to this email address.',
        formSubtitle: 'Enter the verification code sent to {{identifier}}',
        formTitle: 'Verification code',
        resendButton: "Didn't receive a code? Resend",
        successMessage: 'The email {{identifier}} has been added to your account.',
      },
      emailLink: {
        formHint: 'An email containing a verification link will be sent to this email address.',
        formSubtitle: 'Click on the verification link in the email sent to {{identifier}}',
        formTitle: 'Verification link',
        resendButton: "Didn't receive a link? Resend",
        successMessage: 'The email {{identifier}} has been added to your account.',
      },
      enterpriseSSOLink: {
        formButton: 'Click to sign-in',
        formSubtitle: 'Complete the sign-in with {{identifier}}',
      },
      formHint: "You'll need to verify this email address before it can be added to your account.",
      removeResource: {
        messageLine1: '{{identifier}} will be removed from this account.',
        messageLine2: 'You will no longer be able to sign in using this email address.',
        successMessage: '{{emailAddress}} has been removed from your account.',
        title: 'Remove email address',
      },
      title: 'Add email address',
      verifyTitle: 'Verify email address',
    },
    formButtonPrimary__add: 'Add',
    formButtonPrimary__continue: 'Continue',
    formButtonPrimary__finish: 'Finish',
    formButtonPrimary__remove: 'Remove',
    formButtonPrimary__save: 'Save',
    formButtonReset: 'Cancel',
    mfaPage: {
      formHint: 'Select a method to add.',
      title: 'Add two-step verification',
    },
    mfaPhoneCodePage: {
      backButton: 'Use existing number',
      primaryButton__addPhoneNumber: 'Add phone number',
      removeResource: {
        messageLine1: '{{identifier}} will be no longer receiving verification codes when signing in.',
        messageLine2: 'Your account may not be as secure. Are you sure you want to continue?',
        successMessage: 'SMS code two-step verification has been removed for {{mfaPhoneCode}}',
        title: 'Remove two-step verification',
      },
      subtitle__availablePhoneNumbers:
        'Select an existing phone number to register for SMS code two-step verification or add a new one.',
      subtitle__unavailablePhoneNumbers:
        'There are no available phone numbers to register for SMS code two-step verification, please add a new one.',
      successMessage1:
        'When signing in, you will need to enter a verification code sent to this phone number as an additional step.',
      successMessage2:
        'Save these backup codes and store them somewhere safe. If you lose access to your authentication device, you can use backup codes to sign in.',
      successTitle: 'SMS code verification enabled',
      title: 'Add SMS code verification',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Scan QR code instead',
        buttonUnableToScan__nonPrimary: 'Can’t scan QR code?',
        infoText__ableToScan:
          'Set up a new sign-in method in your authenticator app and scan the following QR code to link it to your account.',
        infoText__unableToScan: 'Set up a new sign-in method in your authenticator and enter the Key provided below.',
        inputLabel__unableToScan1:
          'Make sure Time-based or One-time passwords is enabled, then finish linking your account.',
        inputLabel__unableToScan2:
          'Alternatively, if your authenticator supports TOTP URIs, you can also copy the full URI.',
      },
      removeResource: {
        messageLine1: 'Verification codes from this authenticator will no longer be required when signing in.',
        messageLine2: 'Your account may not be as secure. Are you sure you want to continue?',
        successMessage: 'Two-step verification via authenticator application has been removed.',
        title: 'Remove two-step verification',
      },
      successMessage:
        'Two-step verification is now enabled. When signing in, you will need to enter a verification code from this authenticator as an additional step.',
      title: 'Add authenticator application',
      verifySubtitle: 'Enter verification code generated by your authenticator',
      verifyTitle: 'Verification code',
    },
    mobileButton__menu: 'Menu',
    navbar: {
      account: 'Profile',
      apiKeys: 'API keys',
      billing: 'Billing',
      description: 'Manage your account info.',
      security: 'Security',
      title: 'Account',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: '{{name}} will be removed from this account.',
        title: 'Remove passkey',
      },
      subtitle__rename: 'You can change the passkey name to make it easier to find.',
      title__rename: 'Rename Passkey',
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions:
        'It is recommended to sign out of all other devices which may have used your old password.',
      readonly: 'Your password can currently not be edited because you can sign in only via the enterprise connection.',
      successMessage__set: 'Your password has been set.',
      successMessage__signOutOfOtherSessions: 'All other devices have been signed out.',
      successMessage__update: 'Your password has been updated.',
      title__set: 'Set password',
      title__update: 'Update password',
    },
    phoneNumberPage: {
      infoText:
        'A text message containing a verification code will be sent to this phone number. Message and data rates may apply.',
      removeResource: {
        messageLine1: '{{identifier}} will be removed from this account.',
        messageLine2: 'You will no longer be able to sign in using this phone number.',
        successMessage: '{{phoneNumber}} has been removed from your account.',
        title: 'Remove phone number',
      },
      successMessage: '{{identifier}} has been added to your account.',
      title: 'Add phone number',
      verifySubtitle: 'Enter the verification code sent to {{identifier}}',
      verifyTitle: 'Verify phone number',
    },
    plansPage: {
      title: 'Plans',
    },
    profilePage: {
      fileDropAreaHint: 'Recommended size 1:1, up to 10MB.',
      imageFormDestructiveActionSubtitle: 'Remove',
      imageFormSubtitle: 'Upload',
      imageFormTitle: 'Profile image',
      readonly: 'Your profile information has been provided by the enterprise connection and cannot be edited.',
      successMessage: 'Your profile has been updated.',
      title: 'Update profile',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: 'Sign out of device',
        title: 'Active devices',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: 'Reconnect',
        actionLabel__reauthorize: 'Authorize now',
        destructiveActionTitle: 'Remove',
        primaryButton: 'Connect account',
        subtitle__disconnected: 'This account has been disconnected.',
        subtitle__reauthorize:
          'The required scopes have been updated, and you may be experiencing limited functionality. Please re-authorize this application to avoid any issues',
        title: 'Connected accounts',
      },
      dangerSection: {
        deleteAccountButton: 'Delete account',
        title: 'Delete account',
      },
      emailAddressesSection: {
        destructiveAction: 'Remove email',
        detailsAction__nonPrimary: 'Set as primary',
        detailsAction__primary: 'Complete verification',
        detailsAction__unverified: 'Verify',
        primaryButton: 'Add email address',
        title: 'Email addresses',
      },
      enterpriseAccountsSection: {
        primaryButton: 'Connect account',
        title: 'Enterprise accounts',
      },
      headerTitle__account: 'Profile details',
      headerTitle__security: 'Security',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: 'Regenerate',
          headerTitle: 'Backup codes',
          subtitle__regenerate:
            'Get a fresh set of secure backup codes. Prior backup codes will be deleted and cannot be used.',
          title__regenerate: 'Regenerate backup codes',
        },
        phoneCode: {
          actionLabel__setDefault: 'Set as default',
          destructiveActionLabel: 'Remove',
        },
        primaryButton: 'Add two-step verification',
        title: 'Two-step verification',
        totp: {
          destructiveActionTitle: 'Remove',
          headerTitle: 'Authenticator application',
        },
      },
      passkeysSection: {
        menuAction__destructive: 'Remove',
        menuAction__rename: 'Rename',
        primaryButton: 'Add a passkey',
        title: 'Passkeys',
      },
      passwordSection: {
        primaryButton__setPassword: 'Set password',
        primaryButton__updatePassword: 'Update password',
        title: 'Password',
      },
      phoneNumbersSection: {
        destructiveAction: 'Remove phone number',
        detailsAction__nonPrimary: 'Set as primary',
        detailsAction__primary: 'Complete verification',
        detailsAction__unverified: 'Verify phone number',
        primaryButton: 'Add phone number',
        title: 'Phone numbers',
      },
      profileSection: {
        primaryButton: 'Update profile',
        title: 'Profile',
      },
      usernameSection: {
        primaryButton__setUsername: 'Set username',
        primaryButton__updateUsername: 'Update username',
        title: 'Username',
      },
      web3WalletsSection: {
        destructiveAction: 'Remove wallet',
        detailsAction__nonPrimary: 'Set as primary',
        primaryButton: 'Connect wallet',
        title: 'Web3 wallets',
        web3SelectSolanaWalletScreen: {
          subtitle: 'Select a Solana wallet to connect to your account.',
          title: 'Add a Solana wallet',
        },
      },
    },
    usernamePage: {
      successMessage: 'Your username has been updated.',
      title__set: 'Set username',
      title__update: 'Update username',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}} will be removed from this account.',
        messageLine2: 'You will no longer be able to sign in using this web3 wallet.',
        successMessage: '{{web3Wallet}} has been removed from your account.',
        title: 'Remove web3 wallet',
      },
      subtitle__availableWallets: 'Select a web3 wallet to connect to your account.',
      subtitle__unavailableWallets: 'There are no available web3 wallets.',
      successMessage: 'The wallet has been added to your account.',
      title: 'Add web3 wallet',
      web3WalletButtonsBlockButton: '{{provider|titleize}}',
    },
  },
  waitlist: {
    start: {
      actionLink: 'Sign in',
      actionText: 'Already have access?',
      formButton: 'Join the waitlist',
      subtitle: 'Enter your email address and we’ll let you know when your spot is ready',
      title: 'Join the waitlist',
    },
    success: {
      message: 'You will be redirected soon...',
      subtitle: 'We’ll be in touch when your spot is ready',
      title: 'Thanks for joining the waitlist!',
    },
  },
  web3SolanaWalletButtons: {
    connect: 'Connect with {{walletName}}',
    continue: 'Continue with {{walletName}}',
    noneAvailable:
      'No Solana Web3 wallets detected. Please install a Web3 supported {{ solanaWalletsLink || link("wallet extension") }}.',
  },
} as const;
